import {
  Dispatch,
  forwardRef,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import { get, merge } from 'lodash';
import { Model } from 'survey-react-ui';
import {
  ServerValidateQuestionsEvent,
  SurveyModel,
  UploadFilesEvent,
} from 'survey-core';
import { PartialDeep } from 'type-fest';
import { useContextSelector } from 'use-context-selector';

import { SurveyRoot } from '../SurveyRoot/SurveyRoot';
import {
  SurveyOnCompleteCallback,
  SurveyOnNextCallback,
  SurveyState,
  SurveyOnPageChangeCallback,
  SurveyOnUploadFilesCallback,
} from '../Survey.types';
import { ConfigContext } from '../../../contexts/ConfigContext';

import { SurveySurveyInstance } from './SurveySurvey.types';
import { updateAllMatchingPaths } from './SurveySurvey.utils';

import 'survey-core/defaultV2.min.css';

type SurveySurveyProps<T extends object> = PropsWithChildren<{
  json: object;
  onPageChange: SurveyOnPageChangeCallback<T>;
  onComplete: SurveyOnCompleteCallback<T>;
  onNext: SurveyOnNextCallback<T>;
  initialValues: PartialDeep<T>;
  setState: Dispatch<SetStateAction<SurveyState>>;
  onUploadFiles: SurveyOnUploadFilesCallback;
}>;

export const SurveySurvey = forwardRef<
  SurveySurveyInstance,
  SurveySurveyProps<{}>
>(
  (
    {
      json: initialJson,
      onNext,
      onComplete,
      initialValues,
      setState,
      onPageChange,
      onUploadFiles,
    },
    ref
  ) => {
    const choicesByUrl = useContextSelector(
      ConfigContext,
      ({ surveysChoicesByUrlBaseUrl }) => surveysChoicesByUrlBaseUrl
    );

    var json = updateAllMatchingPaths(
      initialJson,
      'choicesByUrl.url',
      (value) => {
        return `${choicesByUrl}${value}`;
      }
    );

    const model = useRef(
      new Model(
        merge({}, json, {
          showPrevButton: false,
          showProgressBar: 'aboveheader',
          widthMode: 'responsive',
          showTitle: false,
          showCompletedPage: false,
        })
      )
    );

    const handleOnCurrentPageChanged = useCallback(
      ({ currentPageNo, pages, data }: SurveyModel) => {
        const currentPage = pages.at(currentPageNo);
        setState((prev) => ({ ...prev, currentPageNo }));
        onPageChange({
          data,
          pageNo: currentPageNo,
          pageName: currentPage.name,
          pageTitle: currentPage.title,
        });
      },
      [setState, onPageChange]
    );

    const handleOnServerValidateQuestions = useCallback(
      async (
        { data, isLastPage }: SurveyModel,
        options: ServerValidateQuestionsEvent
      ) => {
        if (!isLastPage) {
          await onNext({
            data,
            complete: options.complete,
          });
        } else {
          await onComplete({
            data,
            complete: options.complete,
          });
        }
      },
      [onComplete, onNext]
    );

    const handleOnUploadFiles = useCallback(
      (_model: SurveyModel, { name, files, callback }: UploadFilesEvent) => {
        onUploadFiles({ name, files }, ({ files: data = [], error }) => {
          const files = data.map((file) => ({ file }));
          callback(files, error);
        });
      },
      [onUploadFiles]
    );

    useImperativeHandle(ref, () => ({
      prevPage: () => {
        model?.current?.prevPage();
      },
    }));

    useLayoutEffect(() => {
      if (!!initialValues && typeof initialValues === 'object') {
        model.current.data = initialValues;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
      onPageChange({
        pageNo: 0,
        data: initialValues,
        pageName: get(json, ['pages', 0, 'name']),
        pageTitle: get(json, ['pages', 0, 'title']),
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <SurveyRoot
        onCurrentPageChanged={handleOnCurrentPageChanged}
        onServerValidateQuestions={handleOnServerValidateQuestions}
        model={model.current}
        onUploadFiles={handleOnUploadFiles}
      />
    );
  }
);
